import { ApiMetricQueryResponse } from '../../../../common/api/api-interfaces';
import { CachedMasterDataService } from '../../../../common/api/cached-master-data-service';
import { QueryToSqlHelper } from '../../../../common/api/query-to-sql-helper';
import { TimeSliderConfig } from '../../../../common/filter/filter-store';
import {
  ApiMasterDataType,
  RegularMetricId,
} from '../../../../common/graphql/generated/graphql-sdk';
import { MetricStore } from '../../../../common/metrics/metric-store';
import { convertUndefinedFilterValuesToNull } from '../../../../common/utilFunctions/formatters';
import {
  getDimensionFromLabel,
  isHierarchical,
} from '../../../../common/utilFunctions/utils';
import { Filter } from '../../../../common/v2/common/components/filter/filterbar/types';
import { MetricTypes } from '../../../../common/v2/common/types';
import { $HierarchicalFilterSeparator$ } from '../../../../common/v2/constants';
import {
  DataFields,
  DataFieldWithDataType,
  DataTypes,
  EmployeeDataFields,
  Operations,
} from '../../../../common/v2/types';
import { hashCode } from '../../../../common/v2/utils';
import {
  dataTypesToApiMasterDataTypeMapper,
  singleValueTimeSegment,
  timeSliderGranularityToTimeSelectionInput,
} from '../backend-metrics/utils';
import { PbsCellDrillDownContext, SegmentLabels } from '../types';

const EMPLOYEE_ID_FIELD = EmployeeDataFields.EMPLOYEE_ID as string;

/**
 * Validates that a column name contains only word characters and dots
 * (the allowed set for dimension/field names coming from the metric store).
 * Throws if the value contains anything that could break the SQL identifier.
 */
function assertSafeColumnName(column: DataFields): void {
  if (!/^[\w.]+$/.test(column)) {
    throw new Error(`Unsafe column name rejected: "${column}"`);
  }
}

function segmentColumnAndValueToFilter(
  column: DataFields,
  value: string
): Filter {
  assertSafeColumnName(column);
  const filterValue = convertUndefinedFilterValuesToNull(value);
  return {
    dataType: DataTypes.EMPLOYEE,
    property: column,
    operation: Operations.EQUAL,
    values: [filterValue],
    key: hashCode(`${column}:${value}`),
    label: filterValue,
  };
}

/**
 * Resolves the actual DB column name and the leaf-level value for a segment.
 * For hierarchical fields (POSITION, ORGANIZATION, etc.) the column is e.g.
 * POSITION_LEVEL_1 and the value is the last segment in the separator-delimited
 * label.  For flat fields the column is the dataField itself.
 */
function resolveSegmentColumnAndValue(
  dim: DataFieldWithDataType,
  segmentName: SegmentLabels
): { column: DataFields; value: string } {
  if (isHierarchical(dim)) {
    const column = getDimensionFromLabel(segmentName, dim) as DataFields;
    const parts = segmentName.split($HierarchicalFilterSeparator$);
    return { column, value: parts[parts.length - 1] };
  }
  return { column: dim.dataField, value: segmentName };
}

function buildSegmentFilterItems(
  context: PbsCellDrillDownContext,
  segmentLevel1Dimension: DataFieldWithDataType | null,
  segmentLevel2Dimension: DataFieldWithDataType | null
): Filter[] {
  if (
    context.segmentLevel === 1 &&
    context.segmentDimension &&
    context.segmentName
  ) {
    const segment = resolveSegmentColumnAndValue(
      context.segmentDimension,
      context.segmentName
    );
    return [segmentColumnAndValueToFilter(segment.column, segment.value)];
  }

  if (
    context.segmentLevel === 2 &&
    segmentLevel1Dimension &&
    context.segmentLevel1Name &&
    segmentLevel2Dimension &&
    context.segmentDimension &&
    context.segmentName
  ) {
    const parentSegment = resolveSegmentColumnAndValue(
      segmentLevel1Dimension,
      context.segmentLevel1Name
    );
    const childSegment = resolveSegmentColumnAndValue(
      context.segmentDimension,
      context.segmentName
    );
    return [
      segmentColumnAndValueToFilter(parentSegment.column, parentSegment.value),
      segmentColumnAndValueToFilter(childSegment.column, childSegment.value),
    ];
  }

  return [];
}

function buildUserFilters(
  baseFilters: Filter[],
  context: PbsCellDrillDownContext,
  segmentLevel1Dimension: DataFieldWithDataType | null,
  segmentLevel2Dimension: DataFieldWithDataType | null
): string | undefined {
  const segmentFilterItems = buildSegmentFilterItems(
    context,
    segmentLevel1Dimension,
    segmentLevel2Dimension
  );
  const allFilters = [...baseFilters, ...segmentFilterItems];
  return (
    QueryToSqlHelper.getFilterConditions(
      allFilters,
      MetricTypes.REGULAR,
      false
    ) ?? undefined
  );
}

function toNumericMeasureValue(value: unknown): number | null {
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const n = Number(value);
    return Number.isNaN(n) ? null : n;
  }
  return null;
}

/** Per-employee measure contribution for the drilled cell (e.g. age value, count weight). */
function parseEmployeeIdsAndMetricValuesFromResponse(
  response: ApiMetricQueryResponse
): {
  employeeIds: string[];
  metricValueByEmployeeId: Record<string, number>;
} {
  const metricValueByEmployeeId: Record<string, number> = {};
  const employeeIds: string[] = [];
  for (const dp of response.dataPoints ?? []) {
    const raw = dp.measures?.[0]?.value;
    const metricValue = toNumericMeasureValue(raw);
    if (metricValue == null || metricValue === 0) {
      continue;
    }
    const empIdDim = dp.dimensions?.find(
      (d) => (d.property as string) === EMPLOYEE_ID_FIELD
    );
    if (empIdDim?.value == null) {
      continue;
    }
    const v = empIdDim.value;
    const id = typeof v === 'string' ? v : String(v);
    if (metricValueByEmployeeId[id] === undefined) {
      employeeIds.push(id);
    }
    metricValueByEmployeeId[id] = metricValue;
  }
  return { employeeIds, metricValueByEmployeeId };
}

const groupingByEmployeeId = {
  groups: [
    {
      byField: {
        dataField: EMPLOYEE_ID_FIELD,
        dataType: dataTypesToApiMasterDataTypeMapper(
          DataTypes.EMPLOYEE
        ) as ApiMasterDataType,
        filters: null,
      },
      byHierarchicalField: null,
    },
  ],
};

export interface GetEmployeeIdsForPbsCellParams {
  context: PbsCellDrillDownContext;
  filters: Filter[];
  timeSliderConfig: TimeSliderConfig;
  metricStore: MetricStore;
  cachedMasterDataApiService: CachedMasterDataService;
  segmentLevel1Dimension: DataFieldWithDataType | null;
  segmentLevel2Dimension: DataFieldWithDataType | null;
}

export async function getEmployeeIdsForPbsCell(
  params: GetEmployeeIdsForPbsCellParams
): Promise<
  | {
      employeeIds: string[];
      metricValueByEmployeeId: Record<string, number>;
    }
  | { error: string }
> {
  const {
    context,
    filters,
    timeSliderConfig,
    metricStore,
    cachedMasterDataApiService,
    segmentLevel1Dimension,
    segmentLevel2Dimension,
  } = params;

  if (!metricStore.isMetricRegular(context.selectedMeasure)) {
    return { error: 'Not available for this metric type' };
  }

  // MetricId and RegularMetricId are disjoint enums in the generated SDK.
  // isMetricRegular() confirmed it is regular at runtime, so the cross-enum
  // cast is intentional and safe here.
  const queryMeasure: RegularMetricId =
    context.selectedMeasure as unknown as RegularMetricId;

  const timeSelection =
    context.headerType === 'time' && context.dateRange
      ? timeSliderGranularityToTimeSelectionInput({
          ...timeSliderConfig,
          startDate: context.dateRange[0],
          endDate: context.dateRange[1],
        })
      : singleValueTimeSegment(timeSliderConfig);

  const userFilters = buildUserFilters(
    filters,
    context,
    segmentLevel1Dimension,
    segmentLevel2Dimension
  );

  try {
    const result = await cachedMasterDataApiService.queryMetrics(
      timeSelection,
      [queryMeasure],
      userFilters,
      groupingByEmployeeId
    );
    const { employeeIds, metricValueByEmployeeId } =
      parseEmployeeIdsAndMetricValuesFromResponse(result);

    return { employeeIds, metricValueByEmployeeId };
  } catch (err) {
    const message =
      err instanceof Error ? err.message : 'Failed to load employee IDs';
    return { error: message };
  }
}
